import get from 'lodash/get';
import { TRIAD_PROXY_ROUTE } from 'app-requests/apiConstants';
import {
  GetQuestionOptionsParsedResponse,
  School,
  SchoolOptionAPIResponse,
  GetSchoolListingRawResponse,
  OptionsArray,
  GetSchoolListingPayload,
  ErrorPriority,
} from 'types';
import { QUESTION_IDS } from 'consts';
import { LogError } from 'utils/logging/LogError';
import { LogInfo, LogWarning } from 'utils/logging';
import { getGTSAndCTSSchoolObject } from 'utils/form-utils/schoolListingHelpers';
import { getUserSessionId } from 'utils/analyticsHelpers';
import { triadSuperFetch } from 'utils/superFetch';
import { getImageUrlMap } from 'utils/imageHelpers';
import { validatePhone } from 'app-requests/triadmsRequests';

const { MICRO_PORTAL_PRIMARY_SCHOOLS_SELECTION } = QUESTION_IDS;

function getOptionsFromProgramList(
  programData: SchoolOptionAPIResponse['programs'] = []
): OptionsArray {
  if (programData.length === 1) {
    const singleProgramGroup = programData[0];
    return singleProgramGroup.ProgramList
      ? singleProgramGroup.ProgramList.map((program) => {
          return {
            label: program.label,
            value: program.value,
            selected: false,
            guid: program.value,
          };
        })
      : [];
  }
  return programData.map((programGroup) => {
    return {
      group: programGroup.label,
      options: programGroup.ProgramList
        ? programGroup.ProgramList.map((program) => {
            return {
              label: program.label,
              value: program.value,
              selected: false,
              guid: program.value,
            };
          })
        : [],
    };
  });
}
/**
 * Maps the raw school options response from the API to the shape required by the form
 */
function transformSchoolListings(
  schools: SchoolOptionAPIResponse[],
  impressionGroupGuid: string
): School[] {
  return schools.map(
    (school): School => ({
      impressionGroupGuid,
      matchingProgramOptions: getOptionsFromProgramList(school?.programs),
      degreeList:
        school.programs?.map((degree) => ({
          label: degree.label,
          value: degree.value,
          programs:
            degree.ProgramList?.map((program) => ({
              label: program.label,
              value: program.value,
            })) || [],
        })) || [],
      description: school?.schoolDesc?.[0] || '',
      featuredImage: getImageUrlMap(school.featuredImage),
      highlights: school.highlights || '',
      id: school.value,
      label: school.label,
      originalSelectedProgram: {
        label: school.selectedProgram?.label || '',
        value: school.selectedProgram?.value || '',
      },
      rating: school.rating,
      schoolLogo: getImageUrlMap(school.schoolImages),
      terms: school.disclaimer || '',
      impressionGuid: school.impressionGuid,
      hasTcpaCheckbox: school.optIn || false,
      submitText: school.submitText || 'Request Info',
      submissionType: school.submissionType,
      orderBy: school.orderBy,
    })
  );
}

/**
 * @summary given the current state of the form and the current school we may need to get options for a question
 */
export async function getMicroPortalSchoolListing(
  payload: GetSchoolListingPayload,
  schoolCode: string,
  variant: string,
  endpoint: string
): Promise<GetQuestionOptionsParsedResponse> {
  const replies = get(payload, 'questionReplies', []).map(
    ({ questionAnswer, questionId }) => ({
      questionAnswer,
      questionId,
    })
  );

  LogInfo('getMicroPortalSchoolListing', { description: 'Start' });

  if (!getUserSessionId()) {
    LogWarning('getMicroPortalSchoolListing: No user session id found');
  }

  const phoneNumber = replies.find(
    (reply) => reply.questionId === QUESTION_IDS.PRIMARY_PHONE
  )?.questionAnswer?.[0];

  const phoneCheck = await validatePhone(phoneNumber);

  if (!phoneCheck.isValid) {
    LogWarning('getMicroPortalSchoolListing: Invalid phone number', {
      description: `phoneNumber: ${phoneNumber}`,
      priority: ErrorPriority.ONE,
    });

    return {
      [MICRO_PORTAL_PRIMARY_SCHOOLS_SELECTION]: {
        options: [],
        questionId: MICRO_PORTAL_PRIMARY_SCHOOLS_SELECTION,
      },
    };
  }

  const [apiError, response] =
    await triadSuperFetch<GetSchoolListingRawResponse>(
      `${TRIAD_PROXY_ROUTE}${endpoint}`,
      {
        method: 'POST',
        timeout: 12000,
        retry: {
          maxAttempts: 3,
          waitTimeBetweenFails: 2000,
        },
        body: {
          schoolCode,
          requestType: payload.requestType,
          impressionGroup: payload.impressionGroup,
          submittedImpressionGuids: payload.submittedImpressionGuids,
          maxSchools: payload.maxSchools,
          pageTaxonomyValues: payload.pageTaxonomyValues,
          leadEvalToken: payload.leadEvalToken,
          templateName: variant,
          requestedOptions: payload.requestedOptions,
          questionReplies: replies,
        },
      }
    );

  if (apiError) {
    LogInfo('getMicroPortalSchoolListing', { description: 'Failed' });
    throw apiError;
  }

  const { Listings, impressionGroup } = response;

  if (!Listings || !Listings.length) {
    LogInfo('getMicroPortalSchoolListing', {
      description: 'Success No Results',
    });
  } else {
    LogInfo('getMicroPortalSchoolListing', {
      description: 'Success With Results',
    });
  }

  if (Listings && payload.requestType === 'Primary') {
    return {
      [MICRO_PORTAL_PRIMARY_SCHOOLS_SELECTION]: {
        questionId: MICRO_PORTAL_PRIMARY_SCHOOLS_SELECTION,
        options:
          Listings.length === 0
            ? [getGTSAndCTSSchoolObject()]
            : transformSchoolListings(Listings, impressionGroup),
      },
    };
  }

  LogError('getMicroPortalSchoolListing - Listing Not Mapped Correctly', {
    priority: ErrorPriority.ONE,
    description: `Listings: ${Listings}`,
  });

  return {};
}

export const forTestingOnly = {
  getOptionsFromProgramList,
  transformSchoolListings,
};
